import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../utils/apiConfig';
import { buildPhotoUrl } from '../utils/playerCardUtils';
import PlayerCardCanvas from './PlayerCardCanvas';
import '../styles-player-photo-adjust.css';

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const normalizeAdjust = (photoAdjust) => {
  const x = Number(photoAdjust?.x);
  const y = Number(photoAdjust?.y);
  const zoom = Number(photoAdjust?.zoom);
  const rotation = Number(photoAdjust?.rotation);

  return {
    x: Number.isFinite(x) ? x : 0,
    y: Number.isFinite(y) ? y : 0,
    zoom: Number.isFinite(zoom) ? zoom : 1,
    rotation: Number.isFinite(rotation) ? rotation : 0
  };
};

export default function PlayerPhotoAdjustModal({
  open,
  onClose,
  tournament,
  player,
  onSaved
}) {
  const [saving, setSaving] = useState(false);
  const [moveStep, setMoveStep] = useState(5);
  const [zoomStep, setZoomStep] = useState(0.1);
  const [rotateStep, setRotateStep] = useState(5);
  /** 'full' = entire uploaded file (reference); 'frame' = card-shaped crop, drag/zoom here */
  const [leftPanelView, setLeftPanelView] = useState('frame');

  const [draft, setDraft] = useState(() => normalizeAdjust(player?.photoAdjust));
  const previewWrapRef = useRef(null);
  const [previewZoom, setPreviewZoom] = useState(0.7);
  const photoBoxRef = useRef(null);
  const dragRef = useRef(null); // { pointerId, startX, startY, startAdjust:{x,y} }

  useEffect(() => {
    if (!open) return;
    setDraft(normalizeAdjust(player?.photoAdjust));
    setLeftPanelView('frame');
  }, [open, player]);

  useEffect(() => {
    dragRef.current = null;
  }, [leftPanelView]);

  useEffect(() => {
    if (!open) return;

    const el = previewWrapRef.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      const availableW = Math.max(0, rect.width - 16); // padding safety
      const availableH = Math.max(0, rect.height - 16);
      const cardW = Number(tournament?.playerCardDesign?.cardDimensions?.width) || 600;
      const cardH = Number(tournament?.playerCardDesign?.cardDimensions?.height) || 800;
      if (!availableW || !availableH || !cardW || !cardH) return;
      const fit = Math.min(availableW / cardW, availableH / cardH);
      setPreviewZoom(Math.max(0.25, Math.min(2, fit)));
    };

    compute();
    const ro = new ResizeObserver(() => compute());
    ro.observe(el);
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', compute);
    };
  }, [open, tournament]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const photoUrl = useMemo(() => buildPhotoUrl(player?.photo), [player]);
  const photoBoxSize = tournament?.playerCardDesign?.playerPhoto?.size || null;
  const photoBoxW = Number(photoBoxSize?.width) || 180;
  const photoBoxH = Number(photoBoxSize?.height) || 180;
  const photoFrame = tournament?.playerCardDesign?.playerPhoto || {};
  const photoShape = photoFrame?.shape || 'square'; // circle | rounded | square
  const photoBorderWidth = Number(photoFrame?.borderWidth || 0);
  const photoBorderColor = photoFrame?.borderColor || '#ffffff';
  const photoBorderRadius = photoShape === 'circle' ? '50%' : photoShape === 'rounded' ? '12px' : '0px';

  const transform = useMemo(() => {
    const rotationDeg = Number(draft.rotation || 0);
    const x = Number(draft.x || 0);
    const y = Number(draft.y || 0);
    const zoom = Number(draft.zoom || 1);
    return `translate(-50%, -50%) rotate(${rotationDeg}deg) translate(${x}%, ${y}%) scale(${zoom})`;
  }, [draft]);

  if (!open || !player) return null;

  const bump = (partialDelta) => {
    setDraft((prev) => {
      const next = { ...prev };
      if (partialDelta.x) next.x = clamp(Number(prev.x || 0) + partialDelta.x, -100, 100);
      if (partialDelta.y) next.y = clamp(Number(prev.y || 0) + partialDelta.y, -100, 100);
      if (partialDelta.zoom) next.zoom = clamp(Number(prev.zoom || 1) + partialDelta.zoom, 0.5, 3);
      if (partialDelta.rotation) next.rotation = clamp(Number(prev.rotation || 0) + partialDelta.rotation, -180, 180);
      return next;
    });
  };

  const handlePhotoPointerDown = (e) => {
    if (saving) return;
    if (!photoBoxRef.current) return;
    if (!photoUrl) return;

    // Left click / primary touch only
    if (e.button !== undefined && e.button !== 0) return;

    const rect = photoBoxRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    try {
      photoBoxRef.current.setPointerCapture?.(e.pointerId);
    } catch (err) {}

    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      startAdjust: { x: Number(draft.x || 0), y: Number(draft.y || 0) },
      rect: { width: rect.width, height: rect.height }
    };
  };

  const handlePhotoPointerMove = (e) => {
    const state = dragRef.current;
    if (!state) return;
    if (state.pointerId !== e.pointerId) return;
    if (!state.rect?.width || !state.rect?.height) return;

    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;

    // Convert pixels to percent offsets relative to the box size
    const dxPct = (dx / state.rect.width) * 100;
    const dyPct = (dy / state.rect.height) * 100;

    setDraft((prev) => ({
      ...prev,
      x: clamp(state.startAdjust.x + dxPct, -100, 100),
      y: clamp(state.startAdjust.y + dyPct, -100, 100)
    }));
  };

  const handlePhotoPointerUp = (e) => {
    const state = dragRef.current;
    if (!state) return;
    if (state.pointerId !== e.pointerId) return;
    dragRef.current = null;
    try {
      photoBoxRef.current?.releasePointerCapture?.(e.pointerId);
    } catch (err) {}
  };

  const handlePhotoWheel = (e) => {
    if (saving) return;
    // Only zoom when cursor is over the box; keep page from scrolling.
    e.preventDefault();
    const direction = e.deltaY > 0 ? -1 : 1; // scroll up => zoom in
    const step = Number(zoomStep || 0);
    if (!Number.isFinite(step) || step <= 0) return;
    bump({ zoom: direction * step });
  };

  const handleReset = () => setDraft({ x: 0, y: 0, zoom: 1, rotation: 0 });
  const handleAutoFit = () => setDraft((prev) => ({ ...prev, x: 0, y: 0, zoom: 1, rotation: 0 }));

  const handleSave = async () => {
    if (!player?.playerId) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload = {
        photoAdjust: {
          x: Number(draft.x || 0),
          y: Number(draft.y || 0),
          zoom: Number(draft.zoom || 1),
          rotation: Number(draft.rotation || 0)
        }
      };
      const res = await axios.put(`${API_BASE_URL}/api/players/${player.playerId}/photo-adjust`, payload, { headers });
      const updatedPlayer = res.data?.player || null;
      toast.success('Photo adjust saved');
      // Ensure caller can always identify which player was saved.
      onSaved?.({ ...(updatedPlayer || {}), playerId: updatedPlayer?.playerId || player?.playerId }, payload.photoAdjust);
      onClose?.();
    } catch (err) {
      console.error('Failed to save photo adjust', err);
      toast.error(err.response?.data?.message || 'Failed to save photo adjust');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ppam-overlay" onClick={() => (saving ? null : onClose?.())}>
      <div className="ppam-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ppam-header">
          <div className="ppam-title">
            <h3>Adjust player image</h3>
            <div className="ppam-subtitle">
              <strong>{player?.name}</strong>
              <span> · {player?.playerId}</span>
            </div>
          </div>
          <button className="ppam-close" onClick={() => (saving ? null : onClose?.())} disabled={saving} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="ppam-body">
          <div className="ppam-columns">
            <div className="ppam-col ppam-col--left">
              <div className="ppam-col-title">Uploaded image</div>
              <div className="ppam-view-toggle" role="tablist" aria-label="Image view">
                <button
                  type="button"
                  role="tab"
                  aria-selected={leftPanelView === 'full'}
                  className={`ppam-view-toggle-btn${leftPanelView === 'full' ? ' ppam-view-toggle-btn--active' : ''}`}
                  onClick={() => setLeftPanelView('full')}
                  disabled={saving || !photoUrl}
                  title={photoUrl ? 'See the entire uploaded file' : 'No photo'}
                >
                  Full upload
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={leftPanelView === 'frame'}
                  className={`ppam-view-toggle-btn${leftPanelView === 'frame' ? ' ppam-view-toggle-btn--active' : ''}`}
                  onClick={() => setLeftPanelView('frame')}
                  disabled={saving || !photoUrl}
                  title={photoUrl ? 'Drag and scroll to fit the face inside the card frame' : 'No photo'}
                >
                  Card frame
                </button>
              </div>

              {leftPanelView === 'full' && photoUrl && (
                <div className="ppam-full-upload-panel">
                  <p className="ppam-full-upload-hint">
                    Entire upload with the same pan, zoom, and rotate as the card. Switch to <strong>Card frame</strong> to see the cropped card window.
                  </p>
                  <div
                    className="ppam-full-photo-box"
                    ref={photoBoxRef}
                    onPointerDown={handlePhotoPointerDown}
                    onPointerMove={handlePhotoPointerMove}
                    onPointerUp={handlePhotoPointerUp}
                    onPointerCancel={handlePhotoPointerUp}
                    onWheel={handlePhotoWheel}
                  >
                    <img
                      src={photoUrl}
                      alt={`${player?.name || 'Player'} — full upload`}
                      draggable={false}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '50%',
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        transformOrigin: 'center center',
                        transform
                      }}
                    />
                  </div>
                </div>
              )}

              {leftPanelView === 'frame' && (
              <div
                className="ppam-photo-box"
                ref={photoBoxRef}
                style={{
                  '--ppam-photo-aspect': `${photoBoxW} / ${photoBoxH}`,
                  borderRadius: photoBorderRadius,
                  ...(photoBorderWidth > 0 ? { border: `${photoBorderWidth}px solid ${photoBorderColor}` } : {})
                }}
                onPointerDown={handlePhotoPointerDown}
                onPointerMove={handlePhotoPointerMove}
                onPointerUp={handlePhotoPointerUp}
                onPointerCancel={handlePhotoPointerUp}
                onWheel={handlePhotoWheel}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={player?.name || 'Player'}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transformOrigin: 'center center',
                      transform
                    }}
                  />
                ) : (
                  <div className="ppam-empty">No photo</div>
                )}
              </div>
              )}

              <div className="ppam-controls ppam-controls--left">
                <div className="ppam-control-row">
                  <div className="ppam-control-group">
                    <div className="ppam-control-label">Move</div>
                    <div className="ppam-btn-row">
                      <button type="button" onClick={() => bump({ y: -Number(moveStep || 0) })} disabled={saving}>Up</button>
                      <button type="button" onClick={() => bump({ x: -Number(moveStep || 0) })} disabled={saving}>Left</button>
                      <button type="button" onClick={() => bump({ x: Number(moveStep || 0) })} disabled={saving}>Right</button>
                      <button type="button" onClick={() => bump({ y: Number(moveStep || 0) })} disabled={saving}>Down</button>
                    </div>
                  </div>

                  <div className="ppam-control-group">
                    <div className="ppam-control-label">Zoom</div>
                    <div className="ppam-btn-row">
                      <button type="button" onClick={() => bump({ zoom: Number(zoomStep || 0) })} disabled={saving}>Zoom +</button>
                      <button type="button" onClick={() => bump({ zoom: -Number(zoomStep || 0) })} disabled={saving}>Zoom -</button>
                    </div>
                  </div>

                  <div className="ppam-control-group">
                    <div className="ppam-control-label">Rotate (optional)</div>
                    <div className="ppam-btn-row">
                      <button type="button" onClick={() => bump({ rotation: -Number(rotateStep || 0) })} disabled={saving}>Rotate ⟲</button>
                      <button type="button" onClick={() => bump({ rotation: Number(rotateStep || 0) })} disabled={saving}>Rotate ⟳</button>
                    </div>
                  </div>
                </div>

                <div className="ppam-control-row ppam-control-row--meta">
                  <div className="ppam-meta">
                    <label>
                      Move step (%)
                      <input type="number" value={moveStep} min="0.5" step="0.5" onChange={(e) => setMoveStep(Number(e.target.value))} disabled={saving} />
                    </label>
                    <label>
                      Zoom step
                      <input type="number" value={zoomStep} min="0.01" step="0.01" onChange={(e) => setZoomStep(Number(e.target.value))} disabled={saving} />
                    </label>
                    <label>
                      Rotate step (°)
                      <input type="number" value={rotateStep} min="1" step="1" onChange={(e) => setRotateStep(Number(e.target.value))} disabled={saving} />
                    </label>
                    <div className="ppam-current">
                      x: <strong>{Number(draft.x || 0).toFixed(1)}</strong> · y: <strong>{Number(draft.y || 0).toFixed(1)}</strong> · zoom:{' '}
                      <strong>{Number(draft.zoom || 1).toFixed(2)}</strong> · rot: <strong>{Number(draft.rotation || 0).toFixed(0)}°</strong>
                    </div>
                  </div>
                  <div className="ppam-actions">
                    <button type="button" className="ppam-action" onClick={handleAutoFit} disabled={saving}>
                      Auto fit
                    </button>
                    <button type="button" className="ppam-action" onClick={handleReset} disabled={saving}>
                      Reset
                    </button>
                    <button type="button" className="ppam-action ppam-action--primary" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="ppam-col">
              <div className="ppam-col-title">Card preview (real design)</div>
              <div className="ppam-preview-wrap" ref={previewWrapRef}>
                <PlayerCardCanvas
                  design={tournament?.playerCardDesign}
                  tournament={tournament}
                  previewPlayer={{
                    ...(player || {}),
                    // Preserve any existing crop so modal preview matches all cards / exports.
                    photoAdjust: { ...(player?.photoAdjust || {}), ...(draft || {}) }
                  }}
                  selectedElement={null}
                  onElementSelect={() => {}}
                  onDesignChange={() => {}}
                  onShapeChange={() => {}}
                  previewMode={true}
                  previewZoom={previewZoom}
                  adjustPhotoViewMode={leftPanelView === 'full' ? 'full' : 'frame'}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
